import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryParam } from 'use-query-params';
import { type BrandInsightsPageContext } from '@trinity/chat';
import { AuraChatContainer } from '@trinity/chat-components/components/BrandInsights/AuraChat.Container.js';
import { ChatContainer } from '@trinity/chat-components/components/BrandInsights/Chat.Container.js';
import {
  ChatViewType,
  ChatViewTypeContext,
} from '@trinity/chat-components/components/BrandInsights/context.js';
import { LinkStateContainer } from '@trinity/chat-components/components/BrandInsights/Link.Container.js';
import { SegmentChatContainer } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Container.js';
import { SegmentIconCacheLoader } from '@trinity/chat-components/components/BrandInsights/SegmentIconCacheLoader.js';
import {
  ChatPageContextDataContext,
  DispatchPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { ChatHistoryManagerContext } from '@trinity/chat-components/components/Chat.History/context.js';
import { ChatStateContainer } from '@trinity/chat-components/components/Chat.State/Chat.State.js';
import { ChatPendingQueryContextContainer } from '@trinity/chat-components/components/Chat.State/ChatPendingQueryContextContainer.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ContextItemParam } from '@trinity/chat-components/containers/Chat.Widget/context-item.js';
import { useAppSelector } from '@/store';

type Props = ChildrenProps;

type RouteParams = {
  chatIdentifier: string;
};

export const ChatScreenContainer = (props: Props) => {
  const [queryparam] = useQueryParam('ctx', ContextItemParam);
  const apiClient = useChatApiClient();

  const [contextData, setContextData] = useState<BrandInsightsPageContext.Item>(
    initializeContextData(queryparam)
  );

  const canToggleAuraChat = useAppSelector((s) => s.features.chatAuraIframesEnabled);

  return (
    <AuraChatContainer canToggleAuraChat={canToggleAuraChat}>
      <ChatViewTypeContext.Provider value={ChatViewType.Page}>
        <DispatchPageContextDataContext.Provider value={setContextData}>
          <ChatPageContextDataContext.Provider value={contextData}>
            <ChatStateContainer>
              <ChatPendingQueryContextContainer>
                <ChatQueryParamContextContainer>
                  <ChatIdentifierContainer>
                    <SegmentChatContainer>
                      <SegmentIconCacheLoader fetchProjectSegments={apiClient.projects.fetchProjectSegments} />
                      <ChatContainer>{props.children}</ChatContainer>
                    </SegmentChatContainer>
                  </ChatIdentifierContainer>
                </ChatQueryParamContextContainer>
              </ChatPendingQueryContextContainer>
            </ChatStateContainer>
          </ChatPageContextDataContext.Provider>
        </DispatchPageContextDataContext.Provider>
      </ChatViewTypeContext.Provider>
    </AuraChatContainer>
  );
};

function ChatIdentifierContainer(props: ChildrenProps) {
  const { chatIdentifier } = useParams<RouteParams>();

  return <LinkStateContainer initialChatIdentifier={chatIdentifier}>{props.children}</LinkStateContainer>;
}

const ChatQueryParamContextContainer = (props: ChildrenProps) => {
  const [queryparam, setQueryParam] = useQueryParam('ctx', ContextItemParam);
  const { initialized } = useContext(ChatHistoryManagerContext);
  const contextData = useContext(ChatPageContextDataContext);

  useEffect(() => {
    if (
      initialized &&
      contextData &&
      queryparam &&
      JSON.stringify(contextData) === JSON.stringify(queryparam)
    ) {
      setQueryParam(undefined);
    }
  }, [contextData, initialized, queryparam, setQueryParam]);

  return <Fragment>{props.children}</Fragment>;
};

function initializeContextData(qp: BrandInsightsPageContext.Item | null): BrandInsightsPageContext.Item {
  return qp;
}
